import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MemberProvider } from "@/context/MemberContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Vitals from "@/pages/Vitals";
import Trends from "@/pages/Trends";
import Symptoms from "@/pages/Symptoms";
import Diagnostics from "@/pages/Diagnostics";
import Consultations from "@/pages/Consultations";
import Medications from "@/pages/Medications";
import Profiles from "@/pages/Profiles";
import Subscription from "@/pages/Subscription";
import Reports from "@/pages/Reports";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import SharedReport from "@/pages/SharedReport";
import AuthCallback from "@/pages/AuthCallback";
import AdminLogin from "@/pages/AdminLogin";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#7D8F84]">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  // Admins must not access patient health pages (DPDP §11 — purpose limitation)
  if (user.is_admin) return <Navigate to="/admin" replace />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AnyAuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  // Intercept the Emergent Google OAuth return URL synchronously (not in effect)
  // so we process the session_id BEFORE any Protected route can redirect us away.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/admin/login" element={<PublicOnly><AdminLogin /></PublicOnly>} />
      <Route path="/share/:token" element={<SharedReport />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/vitals" element={<Protected><Vitals /></Protected>} />
      <Route path="/trends" element={<Protected><Trends /></Protected>} />
      <Route path="/symptoms" element={<Protected><Symptoms /></Protected>} />
      <Route path="/diagnostics" element={<Protected><Diagnostics /></Protected>} />
      <Route path="/consultations" element={<Protected><Consultations /></Protected>} />
      <Route path="/medications" element={<Protected><Medications /></Protected>} />
      <Route path="/profiles" element={<Protected><Profiles /></Protected>} />
      <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/privacy" element={<AnyAuthRoute><Privacy /></AnyAuthRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MemberProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </MemberProvider>
    </AuthProvider>
  );
}
